/**
 * Workspace: persistent 20:30:50 layout. Document tree at the top
 * of the left column, chat history at the bottom, chat assistant
 * in the middle, variation analysis on the right. Settings opens
 * as a sheet anchored to the chrome so the operator never loses
 * context on the variation under review.
 */

import { useCallback, useState } from "react";
import { DocumentsPanel } from "./DocumentsPanel";
import { AssistantPanel } from "./AssistantPanel";
import { VariationPanel } from "./VariationPanel";
import { ChatHistoryPanel } from "./ChatHistoryPanel";
import { ApprovalsPanel } from "./ApprovalsPanel";
import type { AssessmentRecord, DeterminationPayload } from "../api/client";

export function Workspace({
  onActiveAssessmentChange,
  onActiveDeterminationChange,
}: {
  onActiveAssessmentChange: (a: AssessmentRecord | null) => void;
  onActiveDeterminationChange: (d: DeterminationPayload | null) => void;
}) {
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(() => {
    const stored = sessionStorage.getItem("cvp.ui.chatSession");
    if (stored && stored !== "null" && stored !== "undefined") return stored;
    return null;
  });
  const [selectedDocId, setSelectedDocId] = useState<string | null>(() => {
    return sessionStorage.getItem("cvp.ui.selectedDoc");
  });
  // The variation panel reacts to changes in this id - whenever the
  // operator opens a chat that has an assessment_id linked, this
  // state flips and the variation panel pulls the determination.
  const [chatLinkedAssessmentId, setChatLinkedAssessmentId] = useState<string | null>(null);
  // A monotonic counter that the chat history panel watches so it
  // re-fetches the list when a new session is created or a message
  // lands in the active one.
  const [refreshKey, setRefreshKey] = useState(0);
  // Bumped when the active session is re-pointed in place ("Link to
  // Assistant"). The AssistantPanel keys its session-load effect on
  // this as well as the session id, so it re-reads and the binding
  // chip updates even though the session id did not change. See
  // ADR-0015.
  const [assistantRefreshSignal, setAssistantRefreshSignal] = useState(0);

  const onSelectSession = useCallback((sessionId: string) => {
    setCurrentSessionId(sessionId);
    sessionStorage.setItem("cvp.ui.chatSession", sessionId);
  }, []);

  const onSessionCreated = useCallback((sessionId: string) => {
    setCurrentSessionId(sessionId);
    sessionStorage.setItem("cvp.ui.chatSession", sessionId);
    setRefreshKey((k) => k + 1);
  }, []);

  const onMessageSent = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // The active session was re-pointed at a different variation in
  // place (its id is unchanged). Nudge the AssistantPanel to re-read
  // it and refresh the Chats list so the (possibly renamed) row
  // reflects the new binding.
  const onAssistantSessionRelinked = useCallback(() => {
    setAssistantRefreshSignal((k) => k + 1);
    setRefreshKey((k) => k + 1);
  }, []);

  const onSelectDocument = useCallback((id: string) => {
    setSelectedDocId(id);
    sessionStorage.setItem("cvp.ui.selectedDoc", id);
  }, []);

  // Called by the assistant panel when it loads a session and gets a
  // 404 - the session id stored from a prior backend run is stale.
  const onSessionMissing = useCallback(() => {
    sessionStorage.removeItem("cvp.ui.chatSession");
    setCurrentSessionId(null);
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <div className="grid workspace" style={{ flex: 1 }}>
      <aside className="col col-sidebar col-left-stack" aria-label="Documents and chats">
        <DocumentsPanel
          selectedDocId={selectedDocId}
          onSelectDocument={onSelectDocument}
        />
        <ApprovalsPanel
          refreshKey={refreshKey}
          onSelectAssessment={(assessmentId) => {
            // Store the assessment id so the variation panel
            // picks it up on its next reactive read; the panel
            // already restores from sessionStorage when its
            // active record is null.
            sessionStorage.setItem(
              "cvp.ui.activeAssessment",
              JSON.stringify({ assessment_id: assessmentId }),
            );
            setRefreshKey((k) => k + 1);
          }}
        />
        <ChatHistoryPanel
          currentSessionId={currentSessionId}
          onSelect={onSelectSession}
          refreshKey={refreshKey}
        />
      </aside>
      <VariationPanel
        onActiveAssessmentChange={onActiveAssessmentChange}
        onActiveDeterminationChange={onActiveDeterminationChange}
        chatLinkedAssessmentId={chatLinkedAssessmentId}
        onSessionCreated={onSessionCreated}
        currentSessionId={currentSessionId}
        onAssistantSessionRelinked={onAssistantSessionRelinked}
      />
      <AssistantPanel
        sessionId={currentSessionId}
        onMessageSent={onMessageSent}
        onSessionMissing={onSessionMissing}
        onSessionCreated={onSessionCreated}
        onSessionAssessmentLinked={setChatLinkedAssessmentId}
        refreshSignal={assistantRefreshSignal}
      />
    </div>
  );
}
